// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import {
  Nav,
  OverlayTrigger,
  Tooltip,
  Popover,
} from "react-bootstrap";
import { Button, Card, Form, Col, Row } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faTrashCan,
  faGear,
} from "@fortawesome/pro-solid-svg-icons";

import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import SlideOutMenuSectioned from "@components/SlideOutMenu/SlideOutMenuSectioned";
import { getDate } from "@utils/helperFunctions";
// Client Specific
import useListAccounts from "./api";
import {
  useV2ActivateAccountMutation,
  useV2SwitchAccountMutation,
  useV2ResetAccountMutation,
  useV2ReimportAccountMutation,
  useV2PurgeAccountMutation,
  useV2CancelAccountMutation,
} from "@generated";
import { Account } from "./types";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";

type AccountStatus = 
  | "active"
  | "building"
  | "canceled"
  | "pending"
  | "trial";

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<AccountStatus>("active");
  const [accountToSearch, setAccountToSearch] = useState("");
  const [filter, setFilter] = useState<{ name?: string, status?: AccountStatus[] }>({ status: ["active"] });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activateAccount, {}] = useV2ActivateAccountMutation();
  const [cancelAccount, {}] = useV2CancelAccountMutation();
  const [switchAccount, {}] = useV2SwitchAccountMutation();
  const [resetAccount, {}] = useV2ResetAccountMutation();
  const [reimportAccount, {}] = useV2ReimportAccountMutation();
  const [purgeAccount, {}] = useV2PurgeAccountMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [confirmedAction, setConfirmedAction] = useState("cancellation");
  const [contentAccountSlug, setContentAccountSlug] = useState(null);
  const [currentTab, setCurrentTab] = useState<string>("active");
  const [accountsCount, setAccountsCount] = useState({
    trial: null,
    active: null,
    canceled: null,
  });

  const { accounts, pageMeta, isLoading, refetch } = useListAccounts({
    page: {
      number: pageNumber,
      size: pageSize,
    },
    filter,
  });

  const isAccountStatus = (value: string): value is AccountStatus =>
    ["active", "building", "canceled", "pending", "trial"].includes(value);

  const handleDelete = () => {
    setConfirmedAction(currentTab == "active" ? "cancellation" : "activation");
    setConfirmationMessage(`Are you sure you want to ${currentTab == "active" ? "cancel" : "activate"} ${selectedIds.length} accounts?`);
    setShowConfirmationModal(true);
  };

  const handleReimport = (account) => {
    setConfirmedAction("reimport");
    setContentAccountSlug(account.slug);
    setConfirmationMessage(`Are you sure you want to reimport content for ${account.name}?`);
    setShowConfirmationModal(true);
  }

  const handleReset = (account) => {
    setConfirmedAction("reset");
    setContentAccountSlug(account.slug);
    setConfirmationMessage(`Are you sure you want to clear all content for ${account.name}?`);
    setShowConfirmationModal(true);
  }

  const handlePurge = (account) => {
    setConfirmedAction("purge");
    setContentAccountSlug(account.slug);
    setConfirmationMessage(`Are you sure you want to purge ${account.name} from Scopestack?`);
    setShowConfirmationModal(true);
  }

  const handleConfirm = () => {
    if (confirmedAction == "cancellation" || confirmedAction == "activation") {
      selectedIds.forEach((accountId) => {
        let selectedAccount = accounts.find((c) => c.id == accountId);

        if (selectedAccount) {
          if(currentTab == "active") {
            cancelAccount({ id: selectedAccount.slug })
          } else {
            activateAccount({ id: selectedAccount.slug })
          }
        }
      });
      setSelectedIds([]);
    }
    if (confirmedAction == "reimport") {
      handleAccountReimport();
    }
    if (confirmedAction == "reset") {
      handleAccountReset();
    }
    if (confirmedAction == "purge") {
      handleAccountPurge();
    }
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const searchAccountName = (accountName: string, statusFilter: ("active" | "building" | "canceled" | "pending" | "trial")) => {
    setFilter({ name: accountName, status: [statusFilter] });
  };

  const handleAccountSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchAccountName(accountToSearch, statusFilter);
  };

  const handleAccountSwitch = (account) => {
    switchAccount({ id: account.slug }).then((res) => {
      setSuccessMessage(`You are signed into ${account.name}`);
      setShowSuccessAlert(true);
    })
  }

  const handleAccountReset = () => {
    if (!contentAccountSlug) return;

    resetAccount({ id: contentAccountSlug })
  }

  const handleAccountReimport = () => {
    if (!contentAccountSlug) return;

    reimportAccount({ id: contentAccountSlug })
  }

  const handleAccountPurge = () => {
    if (!contentAccountSlug) return;

    purgeAccount({ id: contentAccountSlug })
  }

  const columns: ColumnDef<Account>[] = [
    { header: "Account Name",
      cell(props) {
        return (
          <div
            className="accountName"
            onClick={() => {
              window.location.assign(`/site/accounts/${props.row.original.slug}/edit`);
              // navigate(`${props.row.original.id}/edit`);
            }}
          >
            {props.row.original.name}
          </div>
        );
      },
    },
    { header: 'Slug',
      cell(props) {
        return (
          <div>{props.row.original.slug}</div>
        );
      }
    },
    { header: 'Customer ID',
      cell(props) {
        return (
          <div>{props.row.original.customerId}</div>
        );
      }
    },
    { header: 'Subscription ID',
      cell(props) {
        return (
          <div>{props.row.original.subscriptionId}</div>
        );
      }
    },
    {
      header: " ",
      cell(props) {
        const account = props.row.original;
        return (
          <div className="accountActions">
            <OverlayTrigger
              trigger="click"
              placement="left"
              rootClose
              overlay={
                <Popover id={`popover-${account.id}`}>
                  <Popover.Header>
                    {account.name} Actions
                  </Popover.Header>
                  <Popover.Body>
                    <div
                      key={"accountSwitch"}
                      style={{ cursor: "pointer", color: "#1c2655" }}
                      onClick={() => {
                          handleAccountSwitch(account);
                      }}
                    >
                      Sign In
                    </div>
                    <hr />
                    <div
                      key={"accountReimport"}
                      style={{ cursor: "pointer", color: "#1c2655" }}
                      onClick={() => {
                          handleReimport(account);
                      }}
                    >
                      Reimport Gold Content
                    </div>
                    <div
                      key={"accountReset"}
                      style={{ cursor: "pointer", color: "#1c2655" }}
                      onClick={() => {
                          handleReset(account);
                      }}
                    >
                      Clear Content
                    </div>
                    {currentTab == "canceled" && (
                      <>
                        <hr />
                        <div
                          key={"accountPurge"}
                          style={{ cursor: "pointer", color: "#1c2655" }}
                          onClick={() => {
                              handlePurge(account);
                          }}
                        >
                          Purge
                        </div>
                      </>
                    )}
                  </Popover.Body>
                </Popover>
              }
            >
              <FontAwesomeIcon icon={faGear} style={{ fontSize: "20px" }} />
            </OverlayTrigger>
          </div>
        )
      }
    },
  ]

  interface Column {
    header: string;
    cell: (props: any) => JSX.Element;
  }

  if (isLoading) {
    return <ScopeStackSpinner />;
  } else {
  return (
    <>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <div className="accountsTable">
        <Card
          style={{
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid var(--gray-3-stroke, #D7D7D7)",
            background: " var(--white, #FFF)",
            boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Card.Header
            style={{
              backgroundColor: "#fff",
              lineHeight: "unset",
              display: "block",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="accordianTitle" style={{ margin: "0" }}>
                Accounts
              </div>
              <Button className="addBtn" onClick={() => window.location.assign("/site/accounts/new")}>Add An Account</Button>
            </div>
            <hr></hr>
            <div
              style={{
                display: "flex",
              }}
            >
              {selectedIds.length > 0 && (
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={handleDelete}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  {currentTab == "active" && (
                    <FontAwesomeIcon style={{ color: "black" }} icon={faTrashCan} />
                  )}
                  {currentTab != "active" && (
                    <FontAwesomeIcon style={{ color: "black" }} icon={faClockRotateLeft} />
                  )}
                </Button>
              )}

              <div style={{ width: "-webkit-fill-available" }}>
                <FormFieldLabel label="Search Accounts" className={undefined} />
                <Form
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      console.log("I should be searching");
                      handleAccountSearch(e);
                    }
                  }}
                  onSubmit={handleAccountSearch}
                >
                  <SearchField
                    placeholder="Search by account name"
                    onChange={(e) => {
                      setPageNumber(1);
                      setAccountToSearch(e.target.value);
                    }}
                    value={accountToSearch}
                    onClick={() => {
                      setAccountToSearch("");
                      searchAccountName("", statusFilter);
                    }}
                    fullWidth
                  />
                </Form>
              </div>
            </div>

          </Card.Header>
          <Nav
            variant="underline"
            defaultActiveKey="active"
            onSelect={(k) => {
              const status: AccountStatus = k && isAccountStatus(k) ? k : "active";
              setCurrentTab(status);
              setSelectedIds([]);
              setStatusFilter(status);
              setPageNumber(1);
              searchAccountName(accountToSearch, status);
            }}
            >
            <Nav.Item>
              <Nav.Link eventKey="active" className="nav-link-account">
                {`Active ${
                  accountsCount?.active !== null && currentTab == "active"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="trial" className="nav-link-account">
                {`Trial ${
                  accountsCount?.trial !== null && currentTab == "trial"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="canceled" className="nav-link-account">
                {`Canceled ${
                  accountsCount?.canceled !== null && currentTab == "canceled"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <DataTable
            data={accounts}
            columns={columns}
            selectable
            bordered
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationEnabled
            pageSizeEnabled
          />
        </Card>
        <ConfirmationModal
          title={`Confirm ${confirmedAction}`}
          message={confirmationMessage}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          show={showConfirmationModal}
          onHide={handleCancel}
        />
      </div>
    </>
  )}
}

export default List;
